import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { ASSETS } from "../config";
import { PRINCIPLES } from "../data";
import { Label, Reveal, SectionHeader, StatusDot } from "../components/primitives";

export default function About() {
  return (
    <main className="pt-16" data-testid="about-page">
      <section className="relative overflow-hidden border-b border-white/[0.06]">
        <img src={ASSETS.ban} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-ink-900/85" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink-900 via-ink-900/40 to-ink-900/70" />
        <div className="relative mx-auto max-w-[1440px] px-5 py-24 md:px-8 md:py-32">
          <Reveal>
            <div className="flex items-center gap-3"><StatusDot /><Label tone="green">About the Protocol</Label></div>
            <h1 className="mt-5 max-w-3xl font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-fog-100 sm:text-5xl lg:text-6xl">
              BUILDING THE <span className="text-pulse-soft">NEURAL LAYER</span>
            </h1>
            <p className="mt-6 max-w-xl text-sm leading-relaxed text-fog-400 sm:text-base">
              Financial infrastructure is becoming programmable. AI agents can reason. Smart contracts can
              execute. But autonomous finance needs a layer that coordinates intelligence, permissions, risk,
              and capital. SYNAPTIX is designed around that missing layer.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="border-b border-white/[0.06] py-20 md:py-28">
        <div className="mx-auto grid max-w-[1440px] gap-12 px-5 md:px-8 lg:grid-cols-2">
          <SectionHeader
            eyebrow="Mission"
            title="COORDINATION IS THE MISSING PRIMITIVE"
            desc="Blockchains made capital programmable. AI made analysis abundant. Neither solved coordination — who decides what an agent may do, with whose capital, under which limits. SYNAPTIX is that coordination layer: mandates in, validated execution out."
          />
          <Reveal delay={0.15}>
            <div className="border border-white/[0.08] bg-panel p-6">
              <Label className="mb-4" tone="gold">The Coordination Problem</Label>
              <div className="space-y-3">
                {[
                  ["Agents without permission", "Unaccountable automation"],
                  ["Capital without intelligence", "Passive, misallocated liquidity"],
                  ["Intent without enforcement", "Promises instead of constraints"],
                  ["SYNAPTIX", "Intelligence + permission + capital, coordinated"],
                ].map(([a, b], i) => (
                  <div key={a} className={`flex items-center justify-between gap-4 border px-4 py-3 ${
                    i === 3 ? "border-pulse/30 bg-pulse/[0.05]" : "border-white/[0.06] bg-ink-900"}`}>
                    <span className={`font-mono text-[11px] tracking-[0.1em] ${i === 3 ? "text-pulse-soft" : "text-fog-400"}`}>{a}</span>
                    <span className={`text-right font-mono text-[10px] uppercase tracking-[0.12em] ${i === 3 ? "text-brass-bright" : "text-fog-600"}`}>{b}</span>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-b border-white/[0.06] bg-ink-850 py-20 md:py-28">
        <div className="mx-auto max-w-[1440px] px-5 md:px-8">
          <SectionHeader
            eyebrow="Design Principles"
            title="FIVE INVARIANTS"
            desc="Non-negotiable properties of the protocol. Every module is designed against these."
          />
          <div className="grid gap-px border border-white/[0.08] bg-white/[0.08] md:grid-cols-2 xl:grid-cols-5">
            {PRINCIPLES.map((p, i) => (
              <Reveal key={p.n} delay={i * 0.07}>
                <div className="flex h-full flex-col bg-panel p-6" data-testid={`principle-${p.n}`}>
                  <span className="font-mono text-[10px] tracking-[0.25em] text-brass">{p.n}</span>
                  <h3 className="mt-4 font-display text-base font-bold leading-snug text-fog-100">{p.title}</h3>
                  <p className="mt-3 text-xs leading-relaxed text-fog-400">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-[1440px] px-5 md:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <Reveal>
              <div className="scanlines relative overflow-hidden border border-white/[0.1]">
                <img src={ASSETS.art} alt="SYNAPTIX network artwork" className="h-full w-full object-cover" loading="lazy" />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink-900/70 to-transparent" />
              </div>
            </Reveal>
            <div>
              <SectionHeader
                eyebrow="The Future"
                title="FROM TOOLS TO TREASURIES"
                desc="The endgame is not a better dashboard. It is multi-agent treasuries that rebalance autonomously, coordinate across protocols, and remain fully accountable to human-defined mandates. Built for the Robinhood Chain ecosystem — where tokenized assets meet real users."
              />
              <Reveal delay={0.2}>
                <Link to="/terminal" data-testid="about-enter-terminal"
                  className="group inline-flex items-center gap-2.5 bg-pulse px-6 py-3.5 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-ink-900 transition-all hover:bg-pulse-soft hover:shadow-[0_0_32px_rgba(0,200,5,0.35)]">
                  Enter Terminal <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                </Link>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
